import { Typography } from '@mui/material';
import { Box, Stack } from '@mui/system';
import { DateTimePicker } from '@mui/x-date-pickers';
import {
  EscalationRule,
  formatResponseExpectation,
  HolidayWindow,
  matchEscalationRule,
  SERVICE_LEVEL_PRIORITY_ORDER,
  TimeWindow,
} from '@provision/sidekick-utils';
import dayjs, { Dayjs } from 'dayjs';
import { useId, useMemo } from 'react';
import { FormFieldLabel } from 'src/components/Forms/FormFieldLabel/form-field-label';
import { SelectMenu, type SelectMenuOption } from 'src/components/SelectMenu';

const SERVICE_LEVEL_OPTIONS: SelectMenuOption[] = SERVICE_LEVEL_PRIORITY_ORDER.map(
  (level) => ({
    value: level,
    title: level,
  }),
);

interface RuleMatchPreviewProps {
  rules: EscalationRule[];
  serviceLevel: string;
  onServiceLevelChange: (value: string) => void;
  currentTime: Dayjs;
  onCurrentTimeChange: (value: Dayjs) => void;
  timezone?: string;
  /**
   * Partner's actual business hours. Used for deadline calculations (e.g., "by end of day").
   */
  businessHours?: TimeWindow[];
  /**
   * Hours during which partners accept escalation notifications.
   * Used for condition matching. Falls back to businessHours when not set.
   */
  escalationCutoffHours?: TimeWindow[];
  holidays?: HolidayWindow[];
}

export const RuleMatchPreview = ({
  rules,
  serviceLevel,
  onServiceLevelChange,
  currentTime,
  onCurrentTimeChange,
  timezone,
  businessHours,
  escalationCutoffHours,
  holidays,
}: RuleMatchPreviewProps) => {
  const serviceLevelTriggerId = useId();
  const previewResult = useMemo(() => {
    if (rules.length === 0) {
      return null;
    }

    const match = matchEscalationRule(rules, serviceLevel, {
      currentTime: currentTime.toDate(),
      timezone,
      businessHours,
      escalationCutoffHours,
      holidays,
    });
    return {
      expectation: formatResponseExpectation(match, serviceLevel, timezone),
      matchedRuleName: match?.rule.name ?? match?.rule.id ?? null,
    };
  }, [
    rules,
    serviceLevel,
    currentTime,
    timezone,
    businessHours,
    escalationCutoffHours,
    holidays,
  ]);

  if (rules.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        backgroundColor: 'action.hover',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        p: 1.5,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          fontWeight: 600,
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          fontSize: '0.6875rem',
          color: 'text.secondary',
          mb: 1,
        }}
      >
        Rule Preview
      </Typography>

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems={{ md: 'flex-end' }}
        spacing={1}
        sx={{ mb: 1.5 }}
      >
        <Stack sx={{ minWidth: 200 }}>
          <FormFieldLabel htmlFor={serviceLevelTriggerId}>Service Level</FormFieldLabel>
          <SelectMenu
            id={serviceLevelTriggerId}
            label="Service Level"
            variant="field"
            menuWidth="trigger"
            options={SERVICE_LEVEL_OPTIONS}
            value={serviceLevel}
            onChange={onServiceLevelChange}
          />
        </Stack>

        <Box sx={{ minWidth: 220 }}>
          <DateTimePicker
            label="Time"
            value={currentTime}
            onChange={(value) => {
              if (value && dayjs(value).isValid()) {
                onCurrentTimeChange(value);
              }
            }}
            slotProps={{ textField: { size: 'small', fullWidth: true } }}
          />
        </Box>
      </Stack>

      {previewResult ? (
        <Box>
          <Typography variant="body2" fontWeight={500}>
            {previewResult.expectation}
          </Typography>
          {previewResult.matchedRuleName ? (
            <Typography variant="caption" color="text.secondary">
              Rule matched: &ldquo;{previewResult.matchedRuleName}&rdquo;
            </Typography>
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
};
